import React from 'react';
import { Box, Chip, Link, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { AssetRow, AssetType } from './types';
import { MetricColOptions, buildMetricCol } from './metricCol';
import {
  assetUrl,
  creativeThumbnailSrc,
  fmtDate,
  fmtInt,
  fmtMoney,
  fmtPct,
  fmtRatio,
  fmtMoneyExact,
  roasTone,
  toNum,
} from './utils';
import CreativeThumbnail from './components/CreativeThumbnail';

// Shares the ad table's column builder — see metricCol.tsx. These used to be
// separate near-identical copies, which is how their tone colours drifted apart.
function num(
  field: keyof AssetRow & string,
  headerName: string,
  format: (v: unknown) => string,
  options: MetricColOptions<AssetRow> = {},
): GridColDef {
  return buildMetricCol<AssetRow>(field, headerName, format, {
    width: 116,
    ...options,
  });
}

// The asset itself. For copy assets that's the text; for image/video it's the
// thumbnail plus whatever name Meta has for it.
function AssetCell({
  row,
  assetType,
  onPreview,
}: {
  row: AssetRow;
  assetType: AssetType;
  onPreview: (row: AssetRow) => void;
}) {
  const isMedia = assetType === 'image' || assetType === 'video';
  const label =
    row.asset_text || row.asset_name || `(untitled ${assetType.replace('_', ' ')})`;

  return (
    <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', py: 0.25 }}>
      {isMedia && (
        <CreativeThumbnail
          src={creativeThumbnailSrc(row.thumb_proxy_url, row.thumbnail_url, row.asset_url)}
          onClick={() => onPreview(row)}
        />
      )}
      <Box sx={{ minWidth: 0 }}>
        <Tooltip title={label} placement="top-start">
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.3,
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {label}
          </Typography>
        </Tooltip>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.5 }}>
          <Chip
            label={`${row.ad_count} ad${row.ad_count === 1 ? '' : 's'}`}
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: 11, '& .MuiChip-label': { px: 0.75 } }}
          />
          {assetUrl(row.asset_url) && (
            <Link
              href={assetUrl(row.asset_url) as string}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{ color: '#6366F1' }}
            >
              View asset
            </Link>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export function buildAssetColumns(
  assetType: AssetType,
  onPreview: (row: AssetRow) => void,
  // Served by the API, so the multiplier is never duplicated in the client.
  multiplier: number | null,
): GridColDef[] {
  return [
    {
      field: 'group_key',
      headerName:
        assetType === 'headline'
          ? 'Headline'
          : assetType === 'primary_text'
          ? 'Primary text'
          : assetType === 'image'
          ? 'Image'
          : 'Video',
      width: 420,
      sortable: false,
      // Read only by the CSV export — the cell renders from the row. Mirrors
      // AssetCell's own label, since a raw group_key for an image or video is a
      // hash that names nothing.
      valueGetter: params => {
        const row = params.row as AssetRow;
        return (
          row.asset_text ||
          row.asset_name ||
          `(untitled ${assetType.replace('_', ' ')})`
        );
      },
      renderCell: params => (
        <AssetCell
          row={params.row as AssetRow}
          assetType={assetType}
          onPreview={onPreview}
        />
      ),
    },
    num('first_seen', 'First seen', fmtDate, {
      width: 124,
      help: 'First day Meta reported delivery for this asset',
      sortAs: 'date',
    }),
    num('last_seen', 'Last seen', fmtDate, { width: 124, sortAs: 'date' }),
    num('spend', 'Spend', fmtMoney),
    num('meta_roas', 'Meta ROAS', fmtRatio, {
      width: 124,
      tone: row => roasTone(row.meta_roas),
      help:
        'Meta-reported purchase value ÷ spend for this asset. Triple Whale attributes revenue to an ad, not to the asset Meta chose to show, so there is no Triple Whale revenue or profit at this level — use the Ad view for margin.',
    }),
    num('true_roas', 'True ROAS', fmtRatio, {
      width: 116,
      tone: row => roasTone(row.true_roas),
      help: `Meta-reported ROAS × ${multiplier ??
        'the incrementality multiplier'}, the uplift measured in the Meta incrementality test. Available at this level because it only ever scales Meta's own numbers — no Triple Whale involved.`,
      cellHelp: row => {
        const metaRoas = toNum(row.meta_roas);
        const result = toNum(row.true_roas);
        if (metaRoas === null || result === null || multiplier === null) return null;
        return `Meta ROAS ${metaRoas.toFixed(2)} × ${multiplier} = ${result.toFixed(2)}`;
      },
    }),
    num('true_revenue', 'True revenue', fmtMoney, {
      width: 132,
      help: `Meta-reported purchase value × ${multiplier ??
        'the incrementality multiplier'}. Hover a value to see the arithmetic.`,
      cellHelp: row => {
        const value = toNum(row.purchase_value);
        const result = toNum(row.true_revenue);
        if (value === null || result === null || multiplier === null) return null;
        return `Meta purchase value ${fmtMoneyExact(value)} × ${multiplier} = ${fmtMoneyExact(
          result,
        )}`;
      },
    }),
    num('purchases', 'Purchases', fmtInt, { width: 118 }),
    num('meta_cpa', 'CPA', fmtMoney, { width: 100 }),
    num('purchase_value', 'Purch. value', fmtMoney, { width: 132 }),
    num('ctr', 'CTR', v => fmtPct(v, 2), { width: 96 }),
    num('cpm', 'CPM', fmtMoney, { width: 96 }),
    num('impressions', 'Impr.', fmtInt, { width: 110 }),
    num('clicks', 'Clicks', fmtInt, { width: 100 }),
    num('ad_count', 'Ads', fmtInt, {
      width: 88,
      help: 'How many distinct ads rotated this asset in the window',
    }),
  ];
}
